import React, { useState } from 'react';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { useUserActions } from '../api/useUserActions';
import { UserGamification } from '../api/types';

// Both match post-admin-coin-adjustment.
const MIN_REASON_LEN = 20;
const REASON_OPTIONS = [
  { code: 'gift', label: 'Gift' },
  { code: 'compensation', label: 'Compensation' },
  { code: 'contest_prize', label: 'Contest prize' },
  { code: 'bug_fix', label: 'Bug fix' },
  { code: 'other', label: 'Other' },
];

interface Props {
  env: Env;
  user: UserGamification;
  onClose: () => void;
  onAdjusted: (newBalance: number) => void;
}

/**
 * Coin adjustment modal — credit (positive) or debit (negative) with a preset
 * reason (a typed note of >= 20 chars is only required for "Other") and a
 * client-generated idempotency key so a double-submit can't double-apply. This is a financial-impact action; the confirm step
 * restates the delta and target balance.
 */
export function CoinAdjustModal({ env, user, onClose, onAdjusted }: Props) {
  const { loading, error, adjustCoins } = useUserActions(env);
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState('');
  const [pickedCode, setPickedCode] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [idempotencyKey] = useState(() =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `adj-${Date.now()}-${Math.round(Math.random() * 1e9)}`,
  );

  const amountNum = Number(amount);
  const amountValid = Number.isInteger(amountNum) && amountNum !== 0;
  const reasonCode = pickedCode ?? (amountNum < 0 ? 'other' : 'gift');
  const noteRequired = reasonCode === 'other';
  const reasonLabel = REASON_OPTIONS.find((o) => o.code === reasonCode)?.label;
  const reasonValid = !noteRequired || reason.trim().length >= MIN_REASON_LEN;
  const canSubmit = amountValid && reasonValid && !loading;

  const submit = async () => {
    const res = await adjustCoins(user.user_id, amountNum, reasonCode, reason.trim(), idempotencyKey);
    if (res) {
      onAdjusted(res.wallet_balance);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-2xl p-5 shadow-xl shadow-black/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Adjust coins</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300" aria-label="Close">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-xs text-gray-400 mb-4">
          User <span className="font-mono text-gray-300">{user.user_id.slice(0, 8)}…</span> · balance{' '}
          <span className="text-white font-semibold">{user.wallet_balance.toLocaleString()}</span> coins
        </p>

        {error && (
          <div className="mb-3">
            <ErrorBanner message={error} />
          </div>
        )}

        {!confirming ? (
          <>
            <label className="block text-xs font-medium text-gray-400 mb-1">Amount (negative to debit)</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 50 or -25"
              className="w-full mb-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
            />
            {!amountValid && amount !== '' && (
              <p className="text-xs text-red-400 mb-2">Enter a non-zero whole number.</p>
            )}

            <label className="block text-xs font-medium text-gray-400 mb-1 mt-3">Reason</label>
            <select
              value={reasonCode}
              onChange={(e) => setPickedCode(e.target.value)}
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
            >
              {REASON_OPTIONS.map((o) => (
                <option key={o.code} value={o.code}>
                  {o.label}
                </option>
              ))}
            </select>

            <label className="block text-xs font-medium text-gray-400 mb-1 mt-3">
              {noteRequired ? `Note (${reason.trim().length}/${MIN_REASON_LEN} min)` : 'Note (optional)'}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={noteRequired ? 'Support correction / fraud reversal — be specific.' : 'Anything worth recording.'}
              className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
            />

            <div className="flex justify-end gap-2 mt-5">
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:text-white">
                Cancel
              </button>
              <button
                onClick={() => setConfirming(true)}
                disabled={!canSubmit}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Review
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-3 bg-gray-950 border border-gray-800 rounded-xl text-sm text-gray-300 mb-4">
              <p>
                {amountNum > 0 ? 'Credit' : 'Debit'}{' '}
                <span className={amountNum > 0 ? 'text-emerald-300 font-semibold' : 'text-red-300 font-semibold'}>
                  {Math.abs(amountNum).toLocaleString()}
                </span>{' '}
                coins → new balance{' '}
                <span className="text-white font-semibold">
                  {(user.wallet_balance + amountNum).toLocaleString()}
                </span>
              </p>
              <p className="mt-2 text-xs text-gray-500">
                {reasonLabel}
                {reason.trim() && ` — ${reason.trim()}`}
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirming(false)}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white"
                disabled={loading}
              >
                Back
              </button>
              <button
                onClick={submit}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40 flex items-center gap-2"
              >
                {loading && <Spinner size="sm" />}
                Confirm adjustment
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
